// pages/AddSalesAndTraffic.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Paper
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplaceUser } from 'module/documents/utils';
import { useDocumentContext } from 'module/documents/context';
import { postWorkplaceDailySummary, getDayParts, getWorkplaceDailySummary } from 'core/API/logbook';

const AddSalesAndTraffic: React.FC = () => {
  const queryClient        = useQueryClient();
  const { showToast }      = useToastContext();
  const history            = useHistory();
  const location           = useLocation();
  const { data, userOrg }  = useWorkplaceUser();
  const { startDate, setStartDate } = useDocumentContext();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');

  // Use the date selected in header calendar, fallback to today if not set
  const today = new Date().toISOString().split('T')[0];
  const currentDate = startDate || today;

  // Handle URL date parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get('date');
    if (dateParam) {
      setStartDate(dateParam);
    }
  }, [location.search, setStartDate]);

  const [error,       setError]        = useState('');

  // Dynamic inputs keyed by day part title (normalized)
  const [salesByPart, setSalesByPart] = useState<Record<string, string>>({});
  const [trafficByPart, setTrafficByPart] = useState<Record<string, string>>({});

  // Fetch all day parts - no filtering needed as API returns all dayparts
  const dayPartsQuery = useQuery(['day_parts'], getDayParts, { staleTime: Infinity });
  const orgDayParts: Array<{ partName: string }> = (dayPartsQuery.data?.member || []).map((dp: any) => ({
    partName: dp.partName
  }));

  const normalizeKey = (k: string) => (k || '').trim().toLowerCase();

  // Input validation functions
  const validateIntegerInput = (value: string): string => {
    // Remove any non-digit characters except for backspace/delete
    return value.replace(/[^0-9]/g, '');
  };

  const handleSalesInputChange = (key: string, value: string) => {
    const validatedValue = validateIntegerInput(value);
    setSalesByPart(prev => ({ ...prev, [key]: validatedValue }));
  };

  const handleTrafficInputChange = (key: string, value: string) => {
    const validatedValue = validateIntegerInput(value);
    setTrafficByPart(prev => ({ ...prev, [key]: validatedValue }));
  };

  // Initialize dynamic maps when org day parts load
  useEffect(() => {
    const partKeys = orgDayParts.map(p => normalizeKey(p.partName));
    if (partKeys.length === 0) return;
    setSalesByPart(prev => {
      const next = { ...prev } as Record<string, string>;
      partKeys.forEach(k => { if (next[k] === undefined) next[k] = ''; });
      return next;
    });
    setTrafficByPart(prev => {
      const next = { ...prev } as Record<string, string>;
      partKeys.forEach(k => { if (next[k] === undefined) next[k] = ''; });
      return next;
    });
  }, [orgDayParts.length]);

  // Fetch existing record for the day to prefill (single record per day)
  const dailySummaryQuery = useQuery(
    ['workplaceDailySummary', workplaceId, currentDate],
    () => getWorkplaceDailySummary({ workplaceId, date: currentDate }),
    { enabled: !!workplaceId && !!currentDate, refetchOnWindowFocus: false }
  );

  // Prefill inputs from existing record's sales/traffic maps
  useEffect(() => {
    const first = dailySummaryQuery.data?.member?.[0];
    if (!first) return;
    const sales = first.sales || {};
    const traffic = first.traffic || {};

    setSalesByPart(prev => {
      const next: Record<string, string> = { ...prev };
      Object.keys(sales).forEach(k => { next[normalizeKey(k)] = String(sales[k] ?? ''); });
      return next;
    });
    setTrafficByPart(prev => {
      const next: Record<string, string> = { ...prev };
      Object.keys(traffic).forEach(k => { next[normalizeKey(k)] = String(traffic[k] ?? ''); });
      return next;
    });
  }, [dailySummaryQuery.data?.member?.[0]?.id]);

  const totalSales = useMemo(() => {
    const sum = Object.values(salesByPart).reduce((sum, v) => sum + (Number(v) || 0), 0);
    return Math.round(sum * 100) / 100; // Round to 2 decimal places
  }, [salesByPart]);

  const totalCustomers = useMemo(() => {
    const sum = Object.values(trafficByPart).reduce((sum, v) => sum + (Number(v) || 0), 0);
    return Math.round(sum * 100) / 100; // Round to 2 decimal places
  }, [trafficByPart]);

  const hasSalesInput   = Object.values(salesByPart).some(v => !!v);
  const hasTrafficInput = Object.values(trafficByPart).some(v => !!v);

  const handleSave = async () => {
    setError('');

    if (!hasSalesInput && !hasTrafficInput) {
      setError('Please fill at least one field in either section.');
      return;
    }

    try {
      // Build dynamic maps from available inputs and org day parts
      const partNames = orgDayParts.map((p) => normalizeKey(p.partName));
      const salesParts: Record<string, number> = {};
      const trafficParts: Record<string, number> = {};
      partNames.forEach((k) => {
        salesParts[k] = Number(salesByPart[k]) || 0;
        trafficParts[k] = Number(trafficByPart[k]) || 0;
      });

      // Use unified API to post both sales and traffic data with dynamic maps
      await postWorkplaceDailySummary({
        workplaceId,
        date: currentDate,
        authorId: 4,
        salesParts,
        trafficParts,
      });

      await queryClient.invalidateQueries(['workplaceDailySummary', workplaceId, currentDate]);

      showToast('success', 'Saved successfully!');
      // Redirect to logbook while preserving the selected date
      history.push(`${ROUTES.LOGBOOK}?date=${currentDate}`);
    } catch (e) {
      console.error(e);
      showToast('error', 'Save failed.');
    }
  };

  return (
    <Box sx={{ bgcolor:'#f4f6fa', minHeight:'100vh', py:3 }}>
      <Box maxWidth="1100px" mx="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          onClick={() => history.goBack()}
          sx={{
            bgcolor: 'rgb(68,99,136)',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 0.75,
            '&:hover': { bgcolor: 'rgb(57,80,111)' }
          }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={false}
          sx={{
            backgroundColor: 'rgb(68, 99, 136)',
            color: '#fff',
            px: 4,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
          }}
        >
          Save
        </Button>
      </Box>

        <Paper elevation={1}>
          {/* Header like "Add Shift Note" */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                Add Sales and Traffic Data
              </Typography>
              {/* <Button
                variant="contained"
                onClick={handleSave}
                disabled={salesMut.isLoading || trafficMut.isLoading}
                sx={{
                  backgroundColor: 'rgb(68, 99, 136)',
                  color: '#fff',
                  px: 4,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
                }}
              >
                {salesMut.isLoading || trafficMut.isLoading ? 'Saving…' : 'Save'}
              </Button> */}
            </Box>
          </Box>

          {/* Form body */}
          <Box sx={{ p: 3 }}>
            {/* Headers */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Add Sales Data
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Add Traffic Data
                </Typography>
              </Grid>
            </Grid>

            {/* Row-by-row layout for tab order */}
            <Grid container spacing={2}>
              {/* Day parts rows - Sales and Traffic side by side */}
              {orgDayParts.map((p, index) => {
                const key = normalizeKey(p.partName);
                return (
                  <React.Fragment key={key}>
                    {/* Sales field */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={`${p.partName} Sales ($)`}
                        value={salesByPart[key] || ''}
                        onChange={(e) => {
                          handleSalesInputChange(key, e.target.value);
                        }}
                        fullWidth
                        type="text"
                        tabIndex={index * 2 + 1}
                        inputProps={{
                          pattern: '[0-9]*',
                          inputMode: 'numeric'
                        }}
                      />
                    </Grid>
                    {/* Traffic field */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={`${p.partName} Customer`}
                        value={trafficByPart[key] || ''}
                        onChange={(e) => {
                          handleTrafficInputChange(key, e.target.value);
                        }}
                        fullWidth
                        type="text"
                        tabIndex={index * 2 + 2}
                        inputProps={{
                          pattern: '[0-9]*',
                          inputMode: 'numeric'
                        }}
                      />
                    </Grid>
                  </React.Fragment>
                );
              })}
              
              {/* Total rows */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Total Sale"
                  value={totalSales}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  tabIndex={orgDayParts.length * 2 + 1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Total Customer"
                  value={totalCustomers}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  tabIndex={orgDayParts.length * 2 + 2}
                />
              </Grid>
            </Grid>

            {/* Error message below the form */}
            {error && (
              <Box mt={3}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddSalesAndTraffic;
